import '../../assets/styles/fonts.css';
import '../../assets/styles/theme.css';
import '../../assets/styles/reset.css';
import '../../assets/styles/index.css';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { BaseStyles, ThemeProvider } from '@primer/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isRenderViewPathname, renderApiProfileUiPath } from '@upright/common/renderer/renderer-settings';
import { queryClient } from '@upright/ui/api/common/react-query';
import { PreviousPageProvider } from '@upright/ui/common/generic/hooks/url-handling/previous-page';
import { APPCUES_ID, AppcuesInitializer, shouldEnableAppcues, useAppcues } from '@upright/ui/integrations/appcues';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import 'rc-slider/assets/index.css';
import { lazy, Suspense, useEffect } from 'react';
import { DndProvider } from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import 'react-loading-skeleton/dist/skeleton.css';
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import 'react-tippy/dist/tippy.css';
import 'react-toggle/style.css';
import { AppError } from '../../common/generic/components/app-error';
import { AriaRouterProvider } from '../../common/generic/components/navigation/aria-router-provider';
import { BouAppLoading } from '../../common/generic/components/progress/spinners/bou-app-loading';
import { useScrollHelpers } from '../../common/generic/layout/utilities/scroll-utils';
import { customTheme } from '../../common/generic/theme/custom-theme';
import { useViewSetting } from '../../common/generic/hooks';
import { isUserInfoRequestOngoing } from '../../untidy/redux/selectors';
import RenderApiProfile from '../render-api-profile';
import { RootContextProvider } from './context';
import { MainRoot } from './main';
import { ReduxProvider } from './redux-provider';
import { replaceLegacyUrlParts } from './replace-legacy-url-parts';
import { DMA_ONBOARDING_PATHS } from '@upright/common/iam/users/dma-onboarding-constants';
import { ARVO_DOCS_UI_PATH } from '@upright/common/arvo/docs/paths';
import {
  isEevoHost,
  isEevoStatusHost,
  movedEevoOrigin,
  PLATFORM_ARVO_CONSOLE_PATH,
} from '@upright/common/arvo/public-url';
import { movedDocsPath } from '@upright/ui/views/arvo/old-docs-redirect';
import { EevoFunnelBeacon } from '@upright/ui/views/arvo/analytics/eevo-funnel-beacon';
import { AUTH_PATHS } from '@upright/ui/views/unified-shell/company-profile-route';

const DmaV2OnboardingView = lazy(async () => await import('@upright/ui/views/dma-v2-view/onboarding-wizard'));
const ArvoView = lazy(async () => await import('@upright/ui/views/arvo'));
const ArvoDocsView = lazy(async () => await import('@upright/ui/views/arvo-docs'));
const EevoStatusView = lazy(async () => await import('@upright/ui/views/eevo-status'));

const cache = createCache({
  key: 'css',
  stylisPlugins: [], // disables all stylis plugins, including autoprefixer
});

export default function AppRoot() {
  const pathname = window.location.pathname;

  replaceLegacyUrlParts();

  // The automated upshot browser arriving via the external API endpoint '/profile' just needs RenderApiProfile to take a screenshot.
  // All the data is provided in the URL so there's no need for the server requests initiated by MainContext and RootContext, so we skip those.
  if (pathname === renderApiProfileUiPath) {
    return (
      <CacheProvider value={cache}>
        <ReduxProvider>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <Routes>
                <Route path={renderApiProfileUiPath} element={<RenderApiProfile />} />
              </Routes>
            </QueryClientProvider>
          </BrowserRouter>
        </ReduxProvider>
      </CacheProvider>
    );
  }

  // Eevo's status page (PLT-7963) reads only static files: none of the providers below, which
  // fetch the signed-in user and settings, so it renders while Eevo's and the platform's API are down.
  if (isEevoStatusHost(window.location.hostname)) {
    return (
      <CacheProvider value={cache}>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<BouAppLoading />}>
            <EevoStatusView />
          </Suspense>
        </QueryClientProvider>
      </CacheProvider>
    );
  }

  return (
    <CacheProvider value={cache}>
      <AppError pathname={pathname}>
        {/* Not on Eevo's hosts, which load no third-party scripts (PLT-7905). */}
        {shouldEnableAppcues() && !isEevoHost(window.location.hostname) && (
          <AppcuesInitializer appcuesId={APPCUES_ID} />
        )}
        <ReduxProvider>
          <DndProvider backend={HTML5Backend}>
            <NuqsAdapter>
              <BrowserRouter>
                <AriaRouterProvider>
                  <PreviousPageProvider>
                    <QueryClientProvider client={queryClient}>
                      <ThemeProvider theme={customTheme}>
                        <BaseStyles>
                          <RootContextProvider>
                            <MainRouteOrLoading />
                          </RootContextProvider>
                        </BaseStyles>
                      </ThemeProvider>
                      <ReactQueryDevtoolsMaybe />
                    </QueryClientProvider>
                  </PreviousPageProvider>
                </AriaRouterProvider>
              </BrowserRouter>
            </NuqsAdapter>
          </DndProvider>
        </ReduxProvider>
      </AppError>
    </CacheProvider>
  );
}

function ReactQueryDevtoolsMaybe() {
  const enabled = useViewSetting('internalAdmin.reactQueryDevtools');
  if (!enabled) {
    return null;
  }
  // Keep the fixed bottom-left toggle button out of the render views so a
  // team user previewing an export URL (or any future server-side persisted
  // view setting) can't end up with it printed into exported PDFs/PNGs.
  if (typeof window !== 'undefined' && isRenderViewPathname(window.location.pathname)) {
    return null;
  }
  return <ReactQueryDevtools initialIsOpen={false} position="left" buttonPosition="bottom-left" />;
}

function MainRouteOrLoading() {
  useScrollHelpers();
  useAppcues();

  const isFetchingUserInfo = useSelector(isUserInfoRequestOngoing);

  if (isFetchingUserInfo) {
    return <BouAppLoading />;
  }
  if (isEevoHost(window.location.hostname)) return <EevoRoutes />;
  return (
    <Suspense fallback={<BouAppLoading />}>
      <Routes>
        <Route path={`${DMA_ONBOARDING_PATHS.SIGN_UP}/*`} element={<DmaV2OnboardingView />} />
        {/* The Arvo console brings its own full-screen shell, sign-up and onboarding: views/arvo. */}
        <Route path={`${PLATFORM_ARVO_CONSOLE_PATH}/*`} element={<PlatformArvoConsole />} />
        <Route path="/*" element={<MainRoot />} />
      </Routes>
    </Suspense>
  );
}

/**
 * The console under `/arvo` on a platform host: where it has moved to Eevo's own host (PLT-7477),
 * the same page there, else the console itself (development environments).
 */
function PlatformArvoConsole() {
  const { pathname, search, hash } = useLocation();
  const eevoOrigin = movedEevoOrigin(window.location.hostname);
  useEffect(() => {
    if (eevoOrigin) {
      // The console's former docs pages (`/arvo/docs/…`, `/arvo/agents`), which agents' skills
      // still link to, go to the same docs page on Eevo's host, not to the console there.
      const movedTo = movedDocsPath(pathname);
      const rest = pathname.slice(PLATFORM_ARVO_CONSOLE_PATH.length) || '/';
      window.location.replace(movedTo ? `${eevoOrigin}${movedTo}${hash}` : `${eevoOrigin}${rest}${search}${hash}`);
    }
  }, [eevoOrigin, pathname, search, hash]);
  return eevoOrigin ? <BouAppLoading /> : <ArvoView />;
}

/**
 * The platform pages Eevo's hosts serve until Eevo has a sign-in of its own: log in, sign up, the
 * emailed sign-in and password-reset links, the SSO error page, and the email verification link
 * (`/profile/<username>?verificationCode=…`).
 */
const EEVO_PLATFORM_PATHS = [...AUTH_PATHS, '/profile'];

/**
 * Eevo's own hosts (eevo.io, PLT-7477) serve the console at the site root, its docs, and the
 * platform's sign-in pages, and no other platform page. A link from before the move (`/arvo/…`)
 * lands on the same console page.
 */
function EevoRoutes() {
  return (
    <Suspense fallback={<BouAppLoading />}>
      {/* Eevo's own cookieless conversion funnel, for every visitor (PLT-7780). It is the only
          analytics on Eevo's hosts: no Google Analytics, tag manager, Appcues or cookie banner
          (PLT-7905; the platform's stay off there in `MainRoot`). */}
      <EevoFunnelBeacon />
      <Routes>
        <Route path={`${ARVO_DOCS_UI_PATH}/*`} element={<ArvoDocsView />} />
        <Route path={`${PLATFORM_ARVO_CONSOLE_PATH}/*`} element={<StripPlatformConsolePath />} />
        {EEVO_PLATFORM_PATHS.map(path => (
          <Route key={path} path={`${path}/*`} element={<MainRoot />} />
        ))}
        <Route path="/*" element={<ArvoView />} />
      </Routes>
    </Suspense>
  );
}

function StripPlatformConsolePath() {
  const { pathname, search, hash } = useLocation();
  // The console's former docs pages (`/arvo/docs/…`, `/arvo/agents`) go to the docs, as on the platform.
  const movedTo = movedDocsPath(pathname);
  if (movedTo) return <Navigate to={`${movedTo}${hash}`} replace />;
  const rest = pathname.slice(PLATFORM_ARVO_CONSOLE_PATH.length) || '/';
  return <Navigate to={`${rest}${search}${hash}`} replace />;
}
